import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import {
  DESIGN_CARD_MEDIA_ENTITY_TYPE,
  designBoardOwnerId,
  type CreateDesignCardInput,
  type DesignBoardOwner,
  type DesignCard,
  type DesignCardList,
  type DesignSketch,
  type ListDesignCardsQuery,
  type SaveDesignSketchInput,
  type UpdateDesignCardInput,
} from '@tailoring-crm/shared';
import { useApiClient } from '../api/api-context.js';
import { attachMedia } from '../media/media-api.js';
import {
  createDesignCard,
  deleteDesignCard,
  listDesignCards,
  updateDesignCard,
} from './design-board-api.js';
import { deleteDesignSketch, getDesignSketch, saveDesignSketch } from './design-sketch-api.js';

/**
 * The design-board hooks.
 *
 * `useCreateDesignCard` does two calls, not one: `createDesignCard` writes the card and its
 * `mediaIds`, but the images themselves were uploaded with `entityId: null` — nothing yet points
 * FROM the image back to the card. `attachMedia` is what does that, over the id the create call
 * just returned, so `entityType=design_card, entityId=<card id>` — what every card's own
 * `listMedia` reads by — actually resolves to them.
 */

export const designCardKeys = {
  all: ['design-cards'] as const,
  list: (owner: DesignBoardOwner | null, filters: ListDesignCardsQuery) =>
    [
      'design-cards',
      'list',
      owner?.kind ?? '',
      owner ? designBoardOwnerId(owner) : '',
      filters,
    ] as const,
};

/**
 * One board — an order's or a customer's. Null while nobody has picked one yet:
 * `enabled: false`, not a request against a blank id.
 */
export function useDesignCards(
  owner: DesignBoardOwner | null,
  filters: ListDesignCardsQuery = {},
): UseQueryResult<DesignCardList> {
  const client = useApiClient();
  return useQuery({
    queryKey: designCardKeys.list(owner, filters),
    queryFn: () => listDesignCards(client, owner as DesignBoardOwner, filters),
    enabled: owner !== null,
  });
}

/** Invalidate every board a write can have changed. One helper, every mutation. */
function useDesignCardInvalidation(): () => void {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: designCardKeys.all });
  };
}

export function useCreateDesignCard(): UseMutationResult<
  DesignCard,
  unknown,
  { owner: DesignBoardOwner; input: CreateDesignCardInput }
> {
  const client = useApiClient();
  const invalidate = useDesignCardInvalidation();
  return useMutation({
    mutationFn: async ({
      owner,
      input,
    }: {
      owner: DesignBoardOwner;
      input: CreateDesignCardInput;
    }): Promise<DesignCard> => {
      const card = await createDesignCard(client, owner, input);
      const mediaIds = input.mediaIds ?? [];
      if (mediaIds.length > 0) {
        await attachMedia(client, {
          entityType: DESIGN_CARD_MEDIA_ENTITY_TYPE,
          entityId: card.id,
          mediaIds,
        });
      }
      return card;
    },
    onSuccess: invalidate,
  });
}

export function useUpdateDesignCard(): UseMutationResult<
  DesignCard,
  unknown,
  { cardId: string; patch: UpdateDesignCardInput }
> {
  const client = useApiClient();
  const invalidate = useDesignCardInvalidation();
  return useMutation({
    mutationFn: ({ cardId, patch }: { cardId: string; patch: UpdateDesignCardInput }) =>
      updateDesignCard(client, cardId, patch),
    onSuccess: invalidate,
  });
}

export function useDeleteDesignCard(): UseMutationResult<void, unknown, string> {
  const client = useApiClient();
  const invalidate = useDesignCardInvalidation();
  return useMutation({
    mutationFn: (cardId: string) => deleteDesignCard(client, cardId),
    onSuccess: invalidate,
  });
}

// --- The drawing on a card --------------------------------------------------------------------

/**
 * A sketch is fetched by CARD and only when a canvas opens, which is the whole reason the scene
 * is not a field on the card: a board of twelve drawings would otherwise cost twelve scenes to
 * render twelve thumbnails.
 */
export const designSketchKeys = {
  all: ['design-sketches'] as const,
  one: (cardId: string | null) => ['design-sketches', cardId ?? ''] as const,
};

/**
 * One card's scene. `null` is a real, successful answer — the card has no drawing yet — so the
 * canvas opens blank on it rather than staying in a loading state forever.
 *
 * `staleTime: Infinity` because the canvas is the only thing that writes here and it invalidates
 * on save. Refetching a megabyte of strokes because a window regained focus would, at best,
 * replace the drawing under somebody's cursor with the copy they started from.
 */
export function useDesignSketch(cardId: string | null): UseQueryResult<DesignSketch | null> {
  const client = useApiClient();
  return useQuery({
    queryKey: designSketchKeys.one(cardId),
    queryFn: () => getDesignSketch(client, cardId as string),
    enabled: cardId !== null,
    staleTime: Infinity,
    gcTime: 0,
  });
}

/**
 * Save the drawing, and the card with it.
 *
 * BOTH caches are invalidated, not just the scene's: saving flips the card's `hasSketch` and can
 * swap the picture the board tile shows, so a board left un-invalidated would go on offering
 * "Draw" on a card that now has a drawing.
 */
export function useSaveDesignSketch(): UseMutationResult<
  DesignSketch,
  unknown,
  { cardId: string; input: SaveDesignSketchInput }
> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  const invalidate = useDesignCardInvalidation();
  return useMutation({
    mutationFn: ({ cardId, input }: { cardId: string; input: SaveDesignSketchInput }) =>
      saveDesignSketch(client, cardId, input),
    onSuccess: (_sketch, { cardId }) => {
      void queryClient.invalidateQueries({ queryKey: designSketchKeys.one(cardId) });
      // The tile reads its pictures with the generic media query, which knows nothing about
      // sketches — a redraw changes what that returns, so it is invalidated by hand.
      void queryClient.invalidateQueries({
        queryKey: ['media', DESIGN_CARD_MEDIA_ENTITY_TYPE, cardId],
      });
      invalidate();
    },
  });
}

/** Throw the drawing away. The card, its photographs and its status all stay. */
export function useDeleteDesignSketch(): UseMutationResult<void, unknown, string> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  const invalidate = useDesignCardInvalidation();
  return useMutation({
    mutationFn: (cardId: string) => deleteDesignSketch(client, cardId),
    onSuccess: (_void, cardId) => {
      void queryClient.invalidateQueries({ queryKey: designSketchKeys.one(cardId) });
      void queryClient.invalidateQueries({
        queryKey: ['media', DESIGN_CARD_MEDIA_ENTITY_TYPE, cardId],
      });
      invalidate();
    },
  });
}
